import { WorkEmpty } from '../work-page.tsx';
import './payment-surfaces.css';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocalizedContent } from '../../i18n/localized.ts';
import { paymentApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { SHOWCASE_COMPANY_ID } from '../../../shared/showcase.ts';
import { Alert, Button, LoadingBlock, Pagination, Select, TextInput } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { PdfDownloadButton } from '../pdf-download.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { applyCardFee, formatBasisPointsPercent } from '@shared/calculations/payments.ts';
import { DEFAULT_CURRENCY_CODE, minimumChargeCents } from '@shared/currency.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import type { Customer } from '@shared/types/index.ts';
import { formatNumericInstant } from '../../i18n/datetime.ts';

/**
 * A screenful of ledger entries.
 *
 * The endpoint behind this used to answer with a fixed hundred rows and no way
 * to ask for more, so a customer with a long history simply lost the older end
 * of it. It is paged now, which only helps if the panel offers the pager —
 * hence this and the control at the bottom of the list.
 */
const HISTORY_PAGE_SIZE = 20;

/**
 * The customer page's billing section: card on file (entered here on the
 * processor's own page, or saved by the customer from an emailed, texted or
 * copied link; removed), one-off charges, and the customer's payment history.
 */
export default function BillingCard({ customer, onChanged }: { customer: Customer; onChanged: () => void }) {
  const { t } = useTranslation();
  /*
    The company's own payment-method labels, read by whoever is recording the
    money — a seeded, translated library like the room and task ones, so it
    follows the reader (see `localized-text.ts`).
  */
  const localized = useLocalizedContent();
  const { company, can, locations } = useSession();
  const queryClient = useQueryClient();
  // A one-off charge or payment for this customer is taken in their
  // location's currency, which may be its own (0114) — the same answer the
  // Worker reaches, so the amount confirmed here is the amount taken.
  const currency =
    (customer.locationId ? locations.find((location) => location.id === customer.locationId)?.currencyCode : null) ??
    company?.currencyCode ??
    DEFAULT_CURRENCY_CODE;
  const money = (cents: number) => formatCents(cents, currency, getDateLocale());
  // History is formatted in the currency each row was taken in, not the
  // company's current one: a company that switched currencies last month must
  // not have last month's charges relabeled as this month's money.
  const rowMoney = (cents: number, code: string) => formatCents(cents, code, getDateLocale());

  const [error, setError] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * A setup link on screen for the office to hand over, and the fee THAT
   * session recorded.
   *
   * One piece of state rather than two, because the pair has to be true
   * together: a link is handed over with a fee stated beside it, and a second
   * session minted while the first is still on screen would otherwise leave
   * the old URL sitting under the new session's rate — which in the direction
   * that matters means a customer saving a card through a session carrying a
   * higher fee than the office quoted them.
   */
  const [setupLinkShown, setSetupLinkShown] = useState<{
    url: string;
    feeBasisPoints: number;
    /** Whether the office is opening this themselves or handing it over. */
    intent: 'open' | 'share';
  } | null>(null);
  /**
   * A card is being typed into the processor's page in another tab.
   *
   * Only used to decide whether coming back to this one is worth a re-read —
   * see the effect below.
   */
  const [enteringCard, setEnteringCard] = useState(false);
  /**
   * The tab that page is being typed into, opened empty inside the click that
   * asked for it.
   *
   * A tab opened later — when the session comes back from the server — is a
   * popup as far as every browser is concerned, and gets blocked. So the tab
   * is claimed while the click is still on the stack and pointed at the
   * checkout once there is one.
   */
  const cardTabRef = useRef<Window | null>(null);
  const [chargeAmount, setChargeAmount] = useState<number | null>(null);
  const [chargeDescription, setChargeDescription] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordAmount, setRecordAmount] = useState<number | null>(null);
  /** A tip received on top of the amount, offered only with tips switched on. */
  const [recordTip, setRecordTip] = useState<number | null>(null);
  const [recordMethodId, setRecordMethodId] = useState('');
  const [recordDate, setRecordDate] = useState('');
  const [recordNote, setRecordNote] = useState('');

  const status = useQuery({ queryKey: ['payments-status'], queryFn: () => paymentApi.status() });
  const methods = useQuery({
    queryKey: ['payment-methods', 'active'],
    queryFn: () => paymentApi.listMethods(),
    enabled: can('payments:write'),
  });
  const history = useQuery({
    queryKey: ['customer-payments', customer.id, historyPage],
    queryFn: () => paymentApi.customerPayments(customer.id, { page: historyPage, pageSize: HISTORY_PAGE_SIZE }),
    // Keeps the previous page while the next loads, but only within one
    // customer: this card stays mounted when the customer around it changes,
    // and someone else's ledger under this customer's name is worse than a
    // blank one.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === customer.id ? previous : undefined),
  });

  // This card stays mounted when the customer around it changes, and its page
  // number is its own state — the parent resets the quote and schedule pages
  // but cannot reach this one. Without it, opening a customer whose ledger is
  // at least as long leaves you part-way down their history instead of at
  // their newest payments, and no clamp fires because the page exists.
  //
  // The rule, because getting it wrong one field at a time is how this panel
  // has gone wrong repeatedly: EVERY piece of state here is about the customer
  // it was created for, so all of it is cleared when the customer changes.
  // Nothing on this panel names the customer while you are looking at it, and
  // nothing on the processor's hosted page does either — so a leftover is not
  // a stale number somebody will notice, it is an action that quietly lands on
  // the wrong record. A typed charge amount charges the wrong card; a setup
  // link saves a card on the wrong customer; a notice reports something that
  // did not happen here.
  useEffect(() => {
    setHistoryPage(1);
    setEnteringCard(false);
    setSetupLinkShown(null);
    setNotice(null);
    setError(null);
    setChargeAmount(null);
    setChargeDescription('');
    setRecording(false);
    setRecordAmount(null);
    setRecordTip(null);
    setRecordMethodId('');
    setRecordDate('');
    setRecordNote('');
  }, [customer.id]);

  const shared = {
    onError: (caught: unknown) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('billingCard.actionFailed')),
  };
  // Invalidates the whole ledger for this customer rather than refetching the
  // page on screen, because the page on screen is not always the one that
  // changed — a new charge lands on page 1 wherever the reader happens to be.
  function done(message: string | null) {
    setError(null);
    setNotice(message);
    void queryClient.invalidateQueries({ queryKey: ['customer-payments', customer.id] });
    onChanged();
  }

  /**
   * Every way of getting a card saved, through one mutation.
   *
   * They are the same act — mint a setup session for this customer — differing
   * only in who ends up in front of it: `open` puts the office there (the
   * customer is on the telephone reading their card out), `email` and `sms`
   * send the customer to it later, `copy` hands the URL over to be passed on
   * however the office likes. One request, one busy state, one error path, and
   * one form wherever a card is added or replaced — including the "update card
   * on file" a failed visit payment links to, which lands on this section.
   *
   * Whichever route it takes, the number is typed into the PROCESSOR's page. It
   * never reaches this app, this component, or anyone's screen recording of it.
   */
  const setupLink = useMutation({
    // Carrying the customer it was asked for, not just the channel. This panel
    // stays mounted when the customer around it changes, and TanStack runs the
    // callbacks of the LATEST render — so an operator who opens a card page for
    // one customer and clicks through to another before the session comes back
    // would otherwise have that customer's page report an entry that belongs to
    // somebody else, and a hosted page open with no way to tell whose it is.
    mutationFn: ({ channel, customerId }: { channel: 'open' | 'email' | 'sms' | 'copy'; customerId: string }) =>
      paymentApi.setupLink(customerId, {
        ...(channel === 'email' ? { email: true } : {}),
        ...(channel === 'sms' ? { text: true } : {}),
      }),
    onSuccess: (result, { channel, customerId }) => {
      // Not this customer's any more: the click is stale, and nothing here may
      // be said about it on a panel that has moved on. The tab goes with it —
      // a hosted card page naming nobody, left open beside another customer's
      // record, is the one outcome worth being abrupt about.
      if (customerId !== customer.id) {
        cardTabRef.current?.close();
        cardTabRef.current = null;
        return;
      }
      // The fee this SESSION recorded — what the card being saved will agree
      // to — rather than whatever the settings query last read. An owner
      // changing the rate in between would otherwise have the office state a
      // number this card is not recorded as agreeing to.
      const feeNote =
        result.cardFeeBasisPoints > 0
          ? t('billingCard.cardFeeDisclosure', { percent: formatBasisPointsPercent(result.cardFeeBasisPoints) })
          : null;
      const shareable = {
        url: result.url,
        feeBasisPoints: result.cardFeeBasisPoints,
        intent: channel === 'open' ? ('open' as const) : ('share' as const),
      };
      // Whatever was on screen belonged to the session before this one. It goes
      // now, and only the branches below put anything back.
      setSetupLinkShown(null);

      if (channel === 'open') {
        const tab = cardTabRef.current;
        cardTabRef.current = null;
        // A fee has to be READ before the card is typed, and a note left in
        // this tab is a note behind the one that just took focus. So where
        // there is a fee the tab does not go anywhere: the rate this session
        // recorded is stated here with the way on beside it, and opening the
        // processor's page is a second, deliberate click. The same shape the
        // hosted card page itself uses for the same disclosure.
        if (result.cardFeeBasisPoints > 0) {
          tab?.close();
          done(null);
          setSetupLinkShown(shareable);
          return;
        }
        // A browser that refused the tab after all. The session is minted and
        // perfectly usable, so hand the URL over rather than losing it.
        if (!tab || tab.closed) {
          // Not a success: nothing opened. The link is still good, so it is
          // handed over — but as the recovery it is, in the tone that says so.
          done(null);
          setError(t('billingCard.cardEntryBlocked'));
          setSetupLinkShown(shareable);
          return;
        }
        tab.location.replace(result.url);
        setEnteringCard(true);
        // With the fee, where there is one: the page is open but nothing has
        // been typed into it yet, which is exactly when the customer on the
        // other end of the telephone has to be told.
        done([t('billingCard.cardEntryOpened'), feeNote].filter(Boolean).join(' '));
        return;
      }
      if (result.emailed) return done(t('billingCard.cardSetupLinkEmailed'));
      if (result.texted) return done(t('billingCard.cardSetupLinkTexted'));
      if (result.textDeferred) {
        done(t('billingCard.cardSetupLinkTextDeferred'));
        setSetupLinkShown(shareable);
        return;
      }
      // The link exists whatever the provider did with the message, so a text
      // that did not go out hands the office the URL rather than leaving them
      // with nothing — and says so, because otherwise the copy box appearing
      // where a confirmation was expected is the only clue.
      done(channel === 'sms' ? t('billingCard.cardSetupLinkTextNotDelivered') : null);
      setSetupLinkShown(shareable);
    },
    onError: (caught: unknown, { customerId }) => {
      // An empty tab with nothing to put in it would just sit there.
      cardTabRef.current?.close();
      cardTabRef.current = null;
      // Somebody else's failure, on a panel that has moved on: closing the tab
      // is the whole of it, and an error banner about another customer's
      // request would just be confusing.
      if (customerId === customer.id) shared.onError(caught);
    },
  });

  /**
   * Coming back to this tab, once a card has been sent off to be typed.
   *
   * The card lands on the customer's row from the page the OTHER tab finishes
   * on — this one is never told. Re-reading the customer when the office
   * returns to it is what turns "no card on file" into the card they have just
   * entered, without anybody having to know to reload.
   */
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  useEffect(() => {
    if (!enteringCard) return;
    const reread = () => changedRef.current();
    window.addEventListener('focus', reread);
    return () => window.removeEventListener('focus', reread);
  }, [enteringCard]);

  const removeCard = useMutation({
    mutationFn: () => paymentApi.removeCard(customer.id),
    onSuccess: () => done(t('billingCard.cardRemoved')),
    ...shared,
  });
  const record = useMutation({
    mutationFn: () =>
      paymentApi.recordPayment({
        customerId: customer.id,
        ...(recordAmount === null ? {} : { amountCents: recordAmount }),
        ...(recordTip !== null && recordTip > 0 ? { tipCents: recordTip } : {}),
        methodId: recordMethodId,
        ...(recordNote.trim() ? { description: recordNote.trim() } : {}),
        ...(recordDate ? { receivedOn: recordDate } : {}),
      }),
    onSuccess: () => {
      setRecording(false);
      setRecordAmount(null);
      setRecordTip(null);
      setRecordNote('');
      setRecordDate('');
      setHistoryPage(1);
      done(t('billingCard.paymentRecorded'));
    },
    ...shared,
  });
  const [resendingId, setResendingId] = useState<string | null>(null);
  const resendReceipt = useMutation({
    mutationFn: (paymentId: string) => {
      setResendingId(paymentId);
      return paymentApi.resendReceipt(paymentId);
    },
    onSuccess: (result) => {
      setResendingId(null);
      done(t('billingCard.receiptResent', { email: result.resentTo }));
    },
    onError: (caught: unknown) => {
      setResendingId(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('billingCard.actionFailed'));
    },
  });
  /**
   * The third ledger this re-check has to reach, and the last one: a charge
   * made here belongs to no visit and no invoice — `POST /payments/charges`
   * creates it against the customer alone — so a refund of it is filed against
   * the customer alone too, and this history is the only screen it is ever
   * shown on. Without the button here, an ambiguous refund of a one-off charge
   * reserves that charge's amount in front of an operator who can see the row
   * and do nothing about it.
   *
   * Deliberately the same copy as the other two ledgers: it is the same act
   * with the same three answers, and an operator who has met it on a visit
   * should not have to learn it again here.
   */
  const [recheckingId, setRecheckingId] = useState<string | null>(null);
  const recheck = useMutation({
    // Carrying the customer it was asked for, exactly as the setup link above
    // does and for the same reason: this panel stays mounted when the customer
    // around it changes, and TanStack runs the callbacks of the LATEST render.
    // An operator who presses re-check and clicks through to somebody else
    // would otherwise be told that customer's refund had been settled, on the
    // wrong record — and `done()` would refresh the wrong ledger, leaving the
    // row that actually moved reading as unconfirmed until something else
    // refetched it.
    mutationFn: ({ paymentId }: { paymentId: string; customerId: string }) => {
      setRecheckingId(paymentId);
      return paymentApi.recheckRefund(paymentId);
    },
    onSuccess: (payment, { customerId }) => {
      setRecheckingId(null);
      // Not this customer's any more: the answer belongs to a record that is
      // no longer on screen, so it is not announced here. The ledger it does
      // belong to is invalidated by id, so the refund's new state is waiting
      // when somebody goes back to it rather than being lost with the click.
      if (customerId !== customer.id) {
        void queryClient.invalidateQueries({ queryKey: ['customer-payments', customerId] });
        return;
      }
      done(
        payment.status === 'succeeded'
          ? t('appointmentPayments.refundIssued')
          : payment.status === 'pending'
            ? // The provider sometimes says WHY it is unresolved — "this refund
              // needs confirming", which no amount of re-checking will clear by
              // itself. The lookup stores that reason precisely so somebody can
              // act on it, and the generic sentence threw it away.
              payment.failureReason
              ? t('appointmentPayments.refundStillUnconfirmedReason', { reason: payment.failureReason })
              : t('appointmentPayments.refundStillUnconfirmed')
            : t('appointmentPayments.refundNotSent', { reason: payment.failureReason ?? '' }),
      );
    },
    onError: (caught: unknown, { customerId }) => {
      setRecheckingId(null);
      if (customerId !== customer.id) return;
      setError(caught instanceof ApiRequestError ? caught.message : t('billingCard.actionFailed'));
    },
  });
  const charge = useMutation({
    mutationFn: () =>
      paymentApi.createCharge({
        customerId: customer.id,
        amountCents: chargeAmount ?? 0,
        description: chargeDescription || undefined,
      }),
    onSuccess: (payment) => {
      setChargeAmount(null);
      setChargeDescription('');
      // The ledger is newest-first, so a charge made while reading page 2 is
      // written to page 1 — off screen. All the reader sees is the rows under
      // them shift by one, which reads as "it didn't go through" and invites a
      // second charge on someone's card. Go to where the new entry is.
      setHistoryPage(1);
      done(
        payment.status === 'succeeded'
          ? t('billingCard.cardCharged')
          : t('billingCard.chargeStatus', { status: t(`payments.status.${payment.status}`).toLowerCase() }),
      );
    },
    ...shared,
  });

  if (!can('payments:read')) return null;

  const card = customer.cardOnFile;
  const canWrite = can('payments:write');
  const canRefund = can('payments:refund');
  const configured = status.data?.configured === true;
  // An account behind the sender, and the company's own switch left on.
  const companyCanText = Boolean(company?.smsConfigured) && Boolean(company?.smsRemindersEnabled);
  const activeMethods = methods.data?.items ?? [];

  // What the card will actually be charged. The amount typed above is the base;
  // the fee is added server-side, so the button must not promise the base. The
  // rate is this card's, which is not the company's when the card predates a
  // fee change.
  const chargeTotal = applyCardFee(
    chargeAmount ?? 0,
    { enabled: true, basisPoints: history.data?.cardFeeBasisPoints ?? 0 },
    currency,
  );
  // An unanswered query is not a rate of zero, and a charge cannot be undone.
  const feeKnown = history.data !== undefined;

  return (
    <section className="card work-section payment-leaf mb-6" aria-labelledby="billing-heading">
      <div className="work-section-heading">
        <h2 id="billing-heading" className="mb-4 text-lg font-semibold">
          {t('billingCard.heading')}
        </h2>
      </div>

      {/*
        How this customer is billed, when it differs from the residential
        defaults (due on receipt, no PO, an invoice per visit). Edited through
        the customer form; stated here because this card is where anyone
        wondering about their invoices is already looking.
      */}
      {customer.invoiceTermsDays !== null || customer.invoicePoNumber || customer.consolidatedBilling ? (
        <dl className="mb-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-[color:var(--color-muted)]">{t('billingCard.paymentTermsLabel')}</dt>
          <dd>
            {customer.invoiceTermsDays === null
              ? t('billingCard.termsDueOnReceipt')
              : t('billingCard.termsNetDays', { days: customer.invoiceTermsDays })}
          </dd>
          {customer.invoicePoNumber ? (
            <>
              <dt className="text-[color:var(--color-muted)]">{t('billingCard.poNumberLabel')}</dt>
              <dd>{customer.invoicePoNumber}</dd>
            </>
          ) : null}
          {customer.consolidatedBilling ? (
            <>
              <dt className="text-[color:var(--color-muted)]">{t('billingCard.invoicingLabel')}</dt>
              <dd>{t('billingCard.consolidatedMonthly')}</dd>
            </>
          ) : null}
        </dl>
      ) : null}

      {
        <>
          {error ? (
            <div className="mb-4">
              <Alert tone="error">{error}</Alert>
            </div>
          ) : null}
          {notice ? (
            <div className="mb-4">
              <Alert tone="success">{notice}</Alert>
            </div>
          ) : null}
          {setupLinkShown ? (
            <div className="mb-4">
              <Alert
                tone="info"
                title={
                  // Named for what the box is FOR, which is the fee wherever
                  // there is one to read out, and the link itself otherwise —
                  // a popup-blocked entry has no fee to announce.
                  setupLinkShown.feeBasisPoints > 0
                    ? t('billingCard.cardEntryFeeFirstTitle')
                    : t('billingCard.setupLinkTitle')
                }
              >
                <p className="break-all font-mono text-xs">{setupLinkShown.url}</p>
                {/* The emailed version of this link states the fee. A link
                    handed over by hand has to say it somewhere too, and this
                    is the only place the person handing it over will look —
                    at the rate THIS session recorded, which is what the card
                    saved through it will be recorded as agreeing to. */}
                {setupLinkShown.feeBasisPoints > 0 ? (
                  <p className="mt-2 text-sm">
                    {t('billingCard.cardFeeDisclosure', {
                      percent: formatBasisPointsPercent(setupLinkShown.feeBasisPoints),
                    })}
                  </p>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-2">
                  {/* A real click, so the tab it opens is never a popup. */}
                  <Button
                    variant={setupLinkShown.intent === 'open' ? 'primary' : 'secondary'}
                    onClick={() => {
                      window.open(setupLinkShown.url, '_blank', 'noopener');
                      // Armed whatever comes back, because `noopener` makes
                      // that null by specification even when the tab opened
                      // perfectly — and reading it as failure would leave the
                      // office staring at "no card on file" after entering
                      // one. Arming it for a tab that never opened costs a
                      // re-read of the customer nobody notices.
                      setEnteringCard(true);
                      // And the link STAYS. For the same reason the return
                      // value cannot be read: a browser that blocks this one
                      // too would otherwise leave nothing on screen — no card
                      // page, and no URL to copy either. Closing this box is
                      // the copy button's job, which is a decision rather than
                      // a guess.
                    }}
                  >
                    {t('billingCard.openCardPage')}
                  </Button>
                  <Button
                    onClick={() => {
                      void navigator.clipboard.writeText(setupLinkShown.url).catch(() => undefined);
                      setSetupLinkShown(null);
                    }}
                  >
                    {t('billingCard.copyAndClose')}
                  </Button>
                </div>
              </Alert>
            </div>
          ) : null}

          {status.data && !configured && company?.id !== SHOWCASE_COMPANY_ID ? (
            <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('billingCard.notConfigured')}</p>
          ) : null}

          {/* ---- Card on file ------------------------------------------- */}
          {configured ? (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                {card ? (
                  <>
                    <p className="font-medium">
                      {/* A saved method without digits gets no dots — they
                          would be claiming four the app has lost. */}
                      {(card.brand ?? 'Card').toUpperCase()}
                      {card.last4 ? ` •••• ${card.last4}` : ''}
                    </p>
                    <p className="text-sm text-[color:var(--color-muted)]">
                      {card.expMonth && card.expYear
                        ? t('billingCard.expires', { month: card.expMonth, year: card.expYear })
                        : t('billingCard.onFile')}
                    </p>
                  </>
                ) : (
                  <p className="text-[color:var(--color-muted)]">{t('billingCard.noCardOnFile')}</p>
                )}
              </div>
              {canWrite ? (
                <div className="flex flex-wrap gap-2">
                  {/* First, because it is the one action that does not depend
                      on the customer doing something later: the office has
                      them on the telephone now. */}
                  <Button
                    variant="primary"
                    busy={setupLink.isPending && setupLink.variables?.channel === 'open'}
                    disabled={setupLink.isPending}
                    onClick={() => {
                      // Claimed inside the click — see `cardTabRef`. Opened
                      // empty, then pointed at the processor once the session
                      // exists; `opener` is cut first so the page it lands on
                      // has no handle back onto this app.
                      const tab = window.open('', '_blank');
                      if (tab) tab.opener = null;
                      cardTabRef.current = tab;
                      setupLink.mutate({ channel: 'open', customerId: customer.id });
                    }}
                  >
                    {card ? t('billingCard.updateCard') : t('billingCard.addCard')}
                  </Button>
                  {customer.email ? (
                    <Button
                      busy={setupLink.isPending && setupLink.variables?.channel === 'email'}
                      disabled={setupLink.isPending}
                      onClick={() => setupLink.mutate({ channel: 'email', customerId: customer.id })}
                    >
                      {card ? t('billingCard.emailLinkReplace') : t('billingCard.emailCardSetupLink')}
                    </Button>
                  ) : null}
                  {/* Consent lives against one specific number and is only
                      read live by the server, so the button turns on what the
                      *company* can do and a customer who has not opted in gets
                      a sentence back explaining it. */}
                  {companyCanText && customer.phone ? (
                    <Button
                      busy={setupLink.isPending && setupLink.variables?.channel === 'sms'}
                      disabled={setupLink.isPending}
                      onClick={() => setupLink.mutate({ channel: 'sms', customerId: customer.id })}
                    >
                      {card ? t('billingCard.textLinkReplace') : t('billingCard.textCardSetupLink')}
                    </Button>
                  ) : null}
                  <Button
                    busy={setupLink.isPending && setupLink.variables?.channel === 'copy'}
                    disabled={setupLink.isPending}
                    onClick={() => setupLink.mutate({ channel: 'copy', customerId: customer.id })}
                  >
                    {t('billingCard.getLinkToShare')}
                  </Button>
                  {card ? (
                    <Button
                      variant="danger"
                      busy={removeCard.isPending}
                      onClick={() => {
                        if (window.confirm(t('billingCard.confirmRemoveCard'))) removeCard.mutate();
                      }}
                    >
                      {t('billingCard.removeCard')}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* ---- One-off charge ----------------------------------------- */}
          {configured && canWrite && card ? (
            <div className="mb-4 rounded-lg border border-[color:var(--color-line)] p-3">
              <p className="mb-2 text-sm font-semibold">{t('billingCard.oneOffCharge')}</p>
              <div className="grid gap-x-3 sm:grid-cols-2">
                <MoneyInput
                  label={t('billingCard.amount')}
                  valueCents={chargeAmount}
                  currencyCode={currency}
                  onChangeCents={setChargeAmount}
                />
                <TextInput
                  label={t('billingCard.description')}
                  hint={t('billingCard.descriptionHint')}
                  value={chargeDescription}
                  onChange={(event) => setChargeDescription(event.target.value)}
                />
              </div>
              {chargeTotal.feeCents > 0 ? (
                <p className="mb-2 text-sm text-[color:var(--color-muted)]">
                  {t('billingCard.cardFeeNotice', {
                    percent: formatBasisPointsPercent(chargeTotal.basisPoints),
                    fee: money(chargeTotal.feeCents),
                    total: money(chargeTotal.totalCents),
                  })}
                </p>
              ) : null}
              {!feeKnown ? (
                <p className="mb-2 text-sm text-[color:var(--color-muted)]">
                  {history.isError ? t('billingCard.feeUnknown') : t('billingCard.loadingFee')}
                </p>
              ) : null}
              <Button
                variant="primary"
                busy={charge.isPending}
                disabled={!feeKnown || !chargeAmount || chargeAmount < minimumChargeCents(currency)}
                onClick={() => {
                  // The fee-inclusive total, because that is what the card is
                  // about to be charged and the charge cannot be taken back.
                  if (window.confirm(t('billingCard.confirmCharge', { amount: money(chargeTotal.totalCents) })))
                    charge.mutate();
                }}
              >
                {t('billingCard.chargeCard')}
              </Button>
            </div>
          ) : null}

          {/* ---- Record a payment received directly ---------------------- */}
          {canWrite ? (
            <div className="mb-4">
              <Button variant="ghost" onClick={() => setRecording((value) => !value)}>
                {recording ? t('billingCard.recordCancel') : t('billingCard.recordPayment')}
              </Button>
              {recording ? (
                <div className="mt-2 grid gap-2 rounded-lg border border-[color:var(--color-line)] p-3 sm:grid-cols-2">
                  <MoneyInput
                    label={
                      status.data?.tips.enabled
                        ? t('appointmentPayments.recordAmountBeforeTipLabel')
                        : t('billingCard.amount')
                    }
                    valueCents={recordAmount}
                    currencyCode={currency}
                    onChangeCents={setRecordAmount}
                  />
                  <Select
                    label={t('billingCard.recordMethodLabel')}
                    value={recordMethodId}
                    onChange={(event) => setRecordMethodId(event.target.value)}
                    options={[
                      { value: '', label: t('billingCard.recordMethodPlaceholder') },
                      ...activeMethods.map((method) => ({
                        value: method.id,
                        label: localized.text(method.name, method.nameTranslations),
                      })),
                    ]}
                  />
                  {/* Only where this location has tips switched on — the
                      server refuses a tip anywhere else. */}
                  {status.data?.tips.enabled ? (
                    <MoneyInput
                      label={t('billingCard.recordTipLabel')}
                      hint={t('billingCard.recordTipHint')}
                      valueCents={recordTip}
                      currencyCode={currency}
                      onChangeCents={setRecordTip}
                    />
                  ) : null}
                  <TextInput
                    label={t('billingCard.recordDateLabel')}
                    type="date"
                    value={recordDate}
                    onChange={(event) => setRecordDate(event.target.value)}
                  />
                  <TextInput
                    label={t('billingCard.description')}
                    value={recordNote}
                    onChange={(event) => setRecordNote(event.target.value)}
                    maxLength={200}
                  />
                  <div className="sm:col-span-2">
                    <Button
                      variant="primary"
                      busy={record.isPending}
                      disabled={!recordMethodId || recordAmount === null || recordAmount <= 0}
                      onClick={() => record.mutate()}
                    >
                      {t('billingCard.recordSubmit')}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {history.isLoading ? <LoadingBlock /> : null}
          {history.isError ? <Alert tone="error">{(history.error as Error).message}</Alert> : null}
          {/* ---- History ------------------------------------------------- */}
          {history.data && history.data.items.length > 0 ? (
            <ul className="divide-y divide-[color:var(--color-line)] text-sm">
              {history.data.items.map((payment) => (
                <li key={payment.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span>
                    <span className="tabular font-medium">
                      {payment.type === 'refund' ? '−' : ''}
                      {rowMoney(payment.amountCents, payment.currencyCode)}
                    </span>
                    <span className="ml-2 text-[color:var(--color-muted)]">
                      {payment.type === 'refund'
                        ? t('billingCard.refundLabel')
                        : payment.method === 'manual'
                          ? (payment.manualMethodName ?? t('billingCard.recordedLabel'))
                          : payment.method === 'tap_to_pay'
                            ? t('billingCard.tapToPayLabel')
                            : t('billingCard.chargeLabel')}{' '}
                      · {t(`payments.status.${payment.status}`)}
                      {/* The only screen a refund of a parentless charge ever
                          appears on, so a reason shown nowhere else is shown
                          here — an unconfirmed refund waiting on the operator
                          reads as merely slow without it. */}
                      {payment.failureReason ? ` — ${payment.failureReason}` : ''}
                      {payment.tipCents > 0
                        ? ` · ${t('billingCard.tipIncluded', {
                            tip: rowMoney(payment.tipCents, payment.currencyCode),
                          })}`
                        : ''}
                      {payment.description ? ` · ${payment.description}` : ''}
                    </span>
                  </span>
                  <span className="flex flex-wrap items-center gap-2">
                    {payment.type === 'refund' && payment.status === 'pending' && canRefund ? (
                      <Button
                        variant="ghost"
                        busy={recheckingId === payment.id}
                        onClick={() => recheck.mutate({ paymentId: payment.id, customerId: customer.id })}
                      >
                        {t('appointmentPayments.refundRecheck')}
                      </Button>
                    ) : null}
                    {payment.type === 'charge' && payment.status === 'succeeded' ? (
                      <>
                        <PdfDownloadButton url={paymentApi.receiptUrl(payment.id)} variant="link">
                          {t('billingCard.downloadReceipt')}
                        </PdfDownloadButton>
                        {canWrite ? (
                          <Button
                            variant="ghost"
                            busy={resendingId === payment.id}
                            onClick={() => resendReceipt.mutate(payment.id)}
                          >
                            {t('billingCard.resendReceipt')}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                    <span className="text-[color:var(--color-muted)]">{formatNumericInstant(payment.createdAt)}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : history.data && !history.isError ? (
            <WorkEmpty title={t('billingCard.noPaymentsYet')} />
          ) : null}

          <Pagination page={historyPage} totalPages={history.data?.totalPages} onPageChange={setHistoryPage} />
        </>
      }
    </section>
  );
}
